import { Directive, ElementRef, Input, OnChanges, OnInit, Renderer2, SimpleChanges, inject } from '@angular/core';
import { UiComponent } from '@df/ui/ui.component';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import each from 'lodash-es/each';
import { skip } from 'rxjs/internal/operators/skip';

@Directive()
export abstract class AbstractCmsBlockComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges {
    content!: NICmsContentComponent.IData;

    @Input()
    variant?: string;

    protected readonly blockDataUrl?: string;

    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    protected renderer2 = inject(Renderer2);
    protected elementRef = inject(ElementRef);

    applyStylesThrottled!: (data?: any) => void;

    override ngOnInit(): void {
        super.ngOnInit();

        this.applyStylesThrottled = this.timeoutService.throttle(data => this.applyStyles(data), 1000, { leading: true });

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe((event: NICmsDataChange.IChangeDataEvent) => {
                if (
                    this.initiated &&
                    event.model.type === ICmsBlock.type &&
                    (event.changedObject === this.styles ||
                        (['dataSwapOnSize', 'dataInit'].indexOf(event.flag) !== -1 && this.blockDataUrl === (event.model.data as any).url))
                ) {
                    this.applyStylesThrottled(event.model.data as any);
                }
            })
        );

        this.subscriptions.add(this.client.obs.breakpoint.pipe(skip(1)).subscribe(() => this.update()));

        this.initiated = true;

        this.update();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        this.update();

        super.ngOnChanges(changes);
    }

    protected get styles(): NICmsContentComponent.IComponentStyles {
        return (this.content as NICmsContentComponent.IDataWithStyles)?.styles;
    }

    protected update(): void {
        if (this.initiated) {
            // set timeout helps with cls
            this.timeoutService.setTimeout(() => {
                if (this.styles) {
                    this.applyStylesThrottled();
                }
            }, 0);
        }
    }

    protected applyStyles(data?: NICmsContentComponent.IDataWithStyles): void {
        const node = this.elementRef.nativeElement as HTMLElement;

        // `data.styles` is for fallback compatibility
        const styles = this.styles || data?.styles || {};
        // cms component need to be block or inline-block display
        this.renderer2.setStyle(node, 'display', 'block');

        styles.sectionId ? this.renderer2.setAttribute(node, 'id', styles.sectionId) : this.renderer2.removeAttribute(node, 'id');

        each(
            {
                mt: 'margin-top',
                mb: 'margin-bottom',
                ml: 'margin-left',
                mr: 'margin-right',
                pt: 'padding-top',
                pb: 'padding-bottom',
                pl: 'padding-left',
                pr: 'padding-right'
            },
            (property, key) => {
                const value = styles[key];
                value ? this.renderer2.setStyle(node, property, value) : this.renderer2.removeStyle(node, property);
            }
        );

        const styleValue =
            (styles.bg &&
                this.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors.find(color => color.key === styles.bg)?.value) ||
            styles.bg ||
            undefined;
        styleValue ? this.renderer2.setStyle(node, 'background-color', styleValue) : this.renderer2.removeStyle(node, 'background-color');
    }
}
